import React, { useState, useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import EventTypeContext from "../../../contexts/EventTypeContext.js";
import EventHeader from "./EventHeader";
import RegistrationTab from "./RegistrationTab";
import { useSpace } from "../../../contexts/SpaceContext";
import {
  Grid,
  Typography,
  Button,
  Divider as MuiDivider,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { axiosInstance } from "../../../utils/axios";

// Styled component
const Divider = styled(MuiDivider)(({ theme }) => ({
  margin: theme ? theme.spacing(6, 0) : "48px 0",
}));

// GraphQL query to get event type data
const GET_EVENT_TYPE = `
  query GetEventType($id: ID!) {
    getEventType(id: $id) {
      id
      title
      url
      description
      duration
      assignment
      availabilityId
      spaceId
    }
  }
`;

function EventTypeEdit() {
  // Get the event type ID from URL parameters
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSpace } = useSpace();

  // State for component
  const [eventType, setEventType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentTab, setCurrentTab] = useState("Event Setup");

  // Get spaceId from query params or use current space
  const queryParams = new URLSearchParams(location.search);
  const currentSpaceId = queryParams.get("spaceId") || currentSpace?.id;

  // Fetch event type data manually using axiosInstance
  useEffect(() => {
    const fetchEventType = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const response = await axiosInstance.post("/graphql", {
          query: GET_EVENT_TYPE,
          variables: { id },
        });

        // Check for errors in the response
        if (response.data.errors) {
          throw new Error(response.data.errors[0].message);
        }

        // Extract the event type data
        const eventTypeData =
          response.data.getEventType || response.data.data?.getEventType;

        if (!eventTypeData) {
          throw new Error("Event type data not found in response");
        }

        setEventType(eventTypeData);
      } catch (err) {
        console.error("Error fetching event type:", err);
        setError(err.message || "Failed to load event type");
        // Don't navigate away immediately on error
      } finally {
        setLoading(false);
      }
    };

    fetchEventType();
  }, [id]);

  // Use useCallback for stable function references
  const updateUnsavedChanges = useCallback((value) => {
    setHasUnsavedChanges(value);
  }, []);

  const updateCurrentTab = useCallback((tab) => {
    setCurrentTab(tab);
  }, []);

  // If ID is not provided, redirect to the event types list
  useEffect(() => {
    if (!id) {
      navigate(`/calendar/event-types?spaceId=${currentSpaceId}`);
    }
  }, [id, navigate, currentSpaceId]);

  // Show loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box padding={3}>
        <Alert severity="error">
          {error}
          <Button
            color="inherit"
            size="small"
            onClick={() =>
              navigate(`/calendar/event-types?spaceId=${currentSpaceId}`)
            }
            sx={{ ml: 2 }}
          >
            Return to Event Types
          </Button>
        </Alert>
      </Box>
    );
  }

  return (
    <EventTypeContext.Provider
      value={{
        hasUnsavedChanges,
        setHasUnsavedChanges: updateUnsavedChanges,
        currentTab,
        setCurrentTab: updateCurrentTab,
        currentSpaceId,
      }}
    >
      <React.Fragment>
        <Helmet title="Edit Event Type" />
        <Grid justifyContent="space-between" container spacing={10}>
          <Grid item>
            <Typography variant="h3" gutterBottom display="inline">
              Edit Event Type
              {eventType && (
                <Typography
                  variant="h5"
                  color="textSecondary"
                  display="inline"
                  style={{ marginLeft: "10px" }}
                >
                  {eventType.title}
                </Typography>
              )}
            </Typography>
          </Grid>
          <Grid item>
            <div>
              <Button
                variant="contained"
                color="inherit"
                sx={{ mx: "0.5rem" }}
                component={Link}
                to={`/calendar/event-types?spaceId=${currentSpaceId}`}
              >
                <ArrowBackIcon />
                Back
              </Button>
            </div>
          </Grid>
        </Grid>
        <Divider />

        {/* Only render components when event type data is available */}
        {eventType && (
          <>
            <EventHeader eventTypeId={id} />
            <Grid container spacing={6}>
              <Grid item xs={12}>
                <RegistrationTab />
              </Grid>
            </Grid>
          </>
        )}
      </React.Fragment>
    </EventTypeContext.Provider>
  );
}

export default EventTypeEdit;
